import { Box } from '@mui/material';
import UPDatepicker from 'components/datepicker';
import { useTranslation } from 'react-i18next';
import {
  SECURITY__GRACE_PERIOD_ENDED,
  SECURITY__GRACE_PERIOD_ENDS,
  SECURITY__GRACE_PERIOD_HELP,
} from 'translations/constants';
import { hasGracePeriodElapsed, toApiDate, toDateOrNull } from './helpers';
import { ActivationRow, HelpText } from './styles';

interface MfaActivationDateProps {
  /** The API's `yyyy-MM-dd` string, or null. */
  value: string | null;
  /** Last known server value, which tells a stored date apart from one being picked right now. */
  savedValue: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}

const MfaActivationDate = (props: MfaActivationDateProps): JSX.Element => {
  const { value, savedValue, onChange, disabled } = props;
  const [t] = useTranslation();

  /**
   * A stored date that has simply aged past today is the grace period having ended — the normal
   * end state, not a mistake. MUI validates the current value against `minDate` and reds the field,
   * so withholding `minDate` in exactly that case keeps an untouched date from being flagged.
   * The moment the SuperAdmin picks anything the value stops being pristine, `minDate` returns,
   * and a past pick is rejected as US 590 requires.
   */
  const showsStoredElapsedDate = value === savedValue && hasGracePeriodElapsed(value);

  return (
    <ActivationRow>
      <Box>
        <UPDatepicker
          value={toDateOrNull(value)}
          // The server validates against Belgium local time, so a browser in another timezone can
          // disagree about "today". Block the ordinary case here and let the server be the
          // authority on the edge case rather than pre-validating it.
          minDate={showsStoredElapsedDate ? undefined : new Date()}
          disabled={disabled}
          label={t(SECURITY__GRACE_PERIOD_ENDS)}
          onChange={(date: Date | null) => onChange(toApiDate(date))}
        />
      </Box>
      <HelpText>{t(showsStoredElapsedDate ? SECURITY__GRACE_PERIOD_ENDED : SECURITY__GRACE_PERIOD_HELP)}</HelpText>
    </ActivationRow>
  );
};

MfaActivationDate.defaultProps = {
  disabled: false,
};

export default MfaActivationDate;
